import { Injectable, inject, signal, computed } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';
import { TENANT_DISPLAY_NAMES } from '../../shared/constants/tenants';
import { hasAdminRole } from './admin-roles';

export interface User {
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  roles: string[];
  isAdmin: boolean;
  /**
   * M2 Phase 11 Plan 1 (UI-M2-02) — Keycloak `tenant_id` claim value, e.g.
   * `cover3d-direct` or `tryba`. Empty string when the claim is absent
   * (pre-OPS-M2-02 sessions or admins logging in before the mapper ships).
   */
  tenantId: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly keycloak = inject(Keycloak);

  private readonly _user = signal<User | null>(null);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly user = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly isAuthenticated = computed(() => this._user() !== null);
  readonly username = computed(() => this._user()?.username ?? '');
  readonly roles = computed(() => this._user()?.roles ?? []);
  readonly isAdmin = computed(() => this._user()?.isAdmin ?? false);
  /**
   * M2 Phase 11 Plan 1 (UI-M2-02) — Resolved tenant slug straight from the
   * `tenant_id` JWT claim. Empty string for sessions whose Keycloak mapper
   * isn't deployed yet (admin or pre-OPS-M2-02); consumers should treat the
   * empty case as "tenant not yet known" rather than as a specific tenant.
   */
  readonly tenantId = computed(() => this._user()?.tenantId ?? '');
  /**
   * Human label for the active tenant, suitable for tenant-chip / header
   * rendering. Falls back to the raw slug when no display name is configured
   * in {@link TENANT_DISPLAY_NAMES}.
   */
  readonly tenantDisplayName = computed(() => {
    const id = this.tenantId();
    return TENANT_DISPLAY_NAMES[id] ?? id;
  });

  constructor() {
    this.initializeUser();
  }

  private initializeUser(): void {
    if (this.keycloak.authenticated && this.keycloak.tokenParsed) {
      const tokenParsed = this.keycloak.tokenParsed;
      const realmRoles = tokenParsed['realm_access']?.['roles'] ?? [];
      const resourceRoles = tokenParsed['resource_access']?.[environment.keycloak.clientId]?.['roles'] ?? [];
      const allRoles = [...realmRoles, ...resourceRoles];

      const hasAdmin = hasAdminRole(allRoles);

      // M2 Phase 11 Plan 1 (UI-M2-02) — tenantId reads the Keycloak
      // `tenant_id` claim shipped by the OPS-M2-02 user-attribute mapper.
      // The claim is absent on admin sessions and on every session that
      // predates the mapper deploy; we surface '' in that case so consumers
      // can render "Cover3D" generically rather than show empty UI.
      const tenantClaim = (tokenParsed as Record<string, unknown>)['tenant_id'];
      const tenantId = typeof tenantClaim === 'string' ? tenantClaim : '';

      this._user.set({
        username: tokenParsed['preferred_username'] ?? '',
        email: tokenParsed['email'],
        firstName: tokenParsed['given_name'],
        lastName: tokenParsed['family_name'],
        fullName: tokenParsed['name'] ?? tokenParsed['preferred_username'] ?? '',
        roles: allRoles,
        isAdmin: hasAdmin,
        tenantId,
      });
    }
  }

  async loadFullProfile(): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      const profile = await this.keycloak.loadUserProfile();
      const currentUser = this._user();

      if (currentUser) {
        this._user.set({
          ...currentUser,
          email: profile.email ?? currentUser.email,
          firstName: profile.firstName ?? currentUser.firstName,
          lastName: profile.lastName ?? currentUser.lastName,
          fullName: `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || currentUser.username
        });
      }
    } catch (err) {
      this._error.set('Failed to load user profile');
      console.error('Failed to load user profile:', err);
    } finally {
      this._loading.set(false);
    }
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.roles();
    return roles.some(role => userRoles.includes(role));
  }

  hasAllRoles(roles: string[]): boolean {
    const userRoles = this.roles();
    return roles.every(role => userRoles.includes(role));
  }
}
